import axios from 'axios'
import laroute from '~/laroute'
import store from '~/store'
import moment from 'moment'
import { WorkOrder } from '~/_shared/models'

export const state = {
  workOrder: new WorkOrder(),
  workOrders: [],
  workOrderReference: {},
  workOrderSession: {},
  workOrderSessions: [],
  drawings: [],
  wipLocations: [],
  purchaseOrders: [],
  total: 0,
  operations: [],
  pdf: undefined,
  id: 0,
  components: []
}

export const getters = {
  workOrders: state => state.workOrders,
  workOrder: state => state.workOrder,
  workOrderReference: state => state.workOrderReference,
  workOrderSession: state => state.workOrderSession,
  workOrderSessions: state => state.workOrderSessions,
  total: state => state.total,
  drawings: state => state.drawings,
  wipLocations: state => state.wipLocations,
  purchaseOrders: state => state.purchaseOrders,
  operations: state => state.operations,
  pdf: state => state.pdf,
  id: state => state.id,
  components: state => state.components
}

export const mutations = {
  WORK_ORDERS (state, data) {
    state.workOrders = data
  },

  WORK_ORDER (state, data) {
    state.workOrder = Object.assign(new WorkOrder(), data)
  },

  WORK_ORDER_REFERENCE (state, data) {
    state.workOrderReference = data
  },

  WORK_ORDER_SESSION (state, data) {
    state.workOrderSession = data
  },

  WORK_ORDER_SESSIONS (state, data) {
    state.workOrderSessions = data
  },

  TOTAL (state, data) {
    state.total = data
  },

  DRAWINGS (state, data) {
    state.drawings = data
  },

  PURCHASE_ORDERS (state, data) {
    state.purchaseOrders = data
  },

  WIP_LOCATIONS (state, data) {
    state.wipLocations = data
  },

  OPERATIONS (state, data) {
    state.operations = data
  },

  PDF (state, data) {
    state.pdf = data
  },

  MAIN_WO_ID (state, data) {
    state.id = data
  },

  COMPONENTS (state, data) {
    state.components = data
  }
}

export const actions = {
  async fetchAll ({ commit }, filterData) {
    await axios.get(
      laroute.route('work-orders-filter', { companyAbbr: store.getters['auth/company'], ...filterData })
    ).then(res => {
      res.data.data.items.map((workOrder) => {
        workOrder.created_at = moment(workOrder.created_at).format('YYYY-MM-DD')
        workOrder.closed_at = workOrder.closed_at === null ? null : moment(workOrder.closed_at).format('YYYY-MM-DD')
        workOrder.due_date = moment(workOrder.due_date).format('YYYY-MM-DD')
        workOrder.closed_qty = workOrder.closed_qty === null ? 0 : workOrder.closed_qty
        workOrder.is_chosen = false
        workOrder.is_force_closed = workOrder.is_force_closed === null ? false : workOrder.is_force_closed
        workOrder.is_closed = workOrder.is_closed === 1
      })
      commit('WORK_ORDERS', res.data.data.items)
      commit('TOTAL', res.data.data.total)
    })
  },

  async deleteWorkOrder ({ commit }, { id, reason }) {
    await axios.patch(laroute.route('delete-work-order', { id }), { reason })
  },

  async createWOReference ({ commit }, { data }) {
    await axios.post(laroute.route('create-work-order-reference', { companyAbbr: store.getters['auth/company'] }), { data })
      .then((res) => {
        commit('WORK_ORDER_SESSION', res.data.data.woSession)
        commit('WORK_ORDER_REFERENCE', res.data.data.woReference)
      })
  },

  async saveWorkOrders ({ commit }, { items, sessionId, referenceId, externalDetailsData, isManual }) {
    await axios.post(laroute.route('create-work-orders',
      { companyAbbr: store.getters['auth/company'] }), { items, sessionId, referenceId, externalDetailsData, isManual })
      .then((res) => {
        commit('MAIN_WO_ID', res.data.data)
      })
  },

  async getWOReference ({ commit }, id) {
    await axios.get(laroute.route('get-wo-reference', { id }))
      .then((res) => {
        commit('WORK_ORDER_REFERENCE', res.data.data)
      })
  },

  async getWOSessionsForSO ({ commit }, soId) {
    await axios.get(laroute.route('get-wo-sessions-for-so', { soId }))
      .then((res) => {
        commit('WORK_ORDER_SESSIONS', res.data)
      })
  },

  async getWOSession ({ commit }, { id }) {
    await axios.get(laroute.route('get-wo-session', { id }))
      .then((res) => {
        commit('WORK_ORDER_SESSION', res.data)
      })
  },

  async updateWOSession ({ commit }, { id, data }) {
    await axios.patch(laroute.route('update-wo-session', { id }), { data })
  },

  async fetchOne ({ commit }, id) {
    axios.get(laroute.route('get-work-order', { id }))
      .then((res) => {
        res.data.data.workOrder.due_date = moment(res.data.data.workOrder.due_date).format('YYYY-MM-DD')
        res.data.data.drawings.sort((a, b) => (a.detail.sequence > b.detail.sequence) ? 1 : -1)
        commit('WORK_ORDER', res.data.data.workOrder)
        commit('DRAWINGS', res.data.data.drawings)
        commit('COMPONENTS', res.data.data.components)
        commit('OPERATIONS', res.data.data.operations)
      })
  },

  async getWorkOrderData ({ commit }, id) {
    axios.get(laroute.route('get-work-order-data', { id }))
      .then((res) => {
        commit('OPERATIONS', res.data.data)
      })
  },

  async deleteOperation ({ commit }, id) {
    await axios.delete(laroute.route('delete-work-order-operation', { id }))
  },

  async updateWorkOrder ({ commit }, { id, data, items }) {
    await axios.patch(laroute.route('update-work-order', { id }), { data, items })
  },

  async addOperations ({ commit }, { items }) {
    await axios.post(laroute.route('add-work-order-operations'), { items })
  },

  async createWorkOrder ({ commit }, { data, referenceId, isManual }) {
    await axios.post(laroute.route('manual-create-work-order',
      { companyAbbr: store.getters['auth/company'] }), { data, referenceId, isManual }).then((res) => {
      commit('MAIN_WO_ID', res.data.data)
    })
  },

  async updateDueDate ({ commit }, { dueDate, items }) {
    await axios.patch(laroute.route('update-wo-due-date'), { dueDate, items })
  },

  async generatePDF ({ commit }, { id }) {
    await axios.get(laroute.route('generate-work-order-pdf', { id }), { responseType: 'arraybuffer' })
      .then((res) => {
        commit('PDF', res.data)
      })
  },

  async closeWorkOrder ({ commit }, { workOrder }) {
    await axios.post(laroute.route('close-work-order'), { workOrder })
  },

  async undoCloseWorkOrder ({ commit }, { workOrder }) {
    await axios.patch(laroute.route('undo-close-work-order'), { workOrder })
  }
}



// WEBPACK FOOTER //
// ./resources/assets/js/store/modules/workOrder.js